import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, EmptyState, StatusPill } from "../components/Shared";
import { fmtFCFA, fmtDate, ROLES } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash2, Hammer, X } from "lucide-react";
import { toast } from "sonner";
import { TableSkeleton } from "../components/Skeletons";

const emptyForm = { project_id: "", title: "", remarks: "", activities: [{ title: "", cost: "" }] };

export default function AdditionalWorks() {
  const { isRole, user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const canDelete = isRole(ROLES.CEO);
  const canCreate = isRole(ROLES.CEO, ROLES.GM, ROLES.ACEO);
  const needsApproval = isRole(ROLES.GM, ROLES.ACEO) && !isRole(ROLES.CEO);

  const { data: works = [], isLoading } = useQuery({ queryKey: ["additional-works"], queryFn: () => api.get("/additional-works").then((r) => r.data) });
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: () => api.get("/projects").then((r) => r.data) });

  const existingByProject = works.reduce((m, w) => { m[w.project_id] = (m[w.project_id] || 0) + (Number(w.cost) || 0); return m; }, {});
  const totalCost = (f) => f.activities.reduce((s, a) => s + (Number(a.cost) || 0), 0);

  const selectedProject = projects.find((p) => p.id === form.project_id) || null;
  const baseTotal = selectedProject ? (Number(selectedProject.total_cost) || 0) + (existingByProject[selectedProject.id] || 0) : 0;
  const newTotal = baseTotal + totalCost(form);

  const openNew = () => { setForm(emptyForm); setOpen(true); };
  const setActivity = (i, field, val) => setForm((f) => ({ ...f, activities: f.activities.map((a, idx) => (idx === i ? { ...a, [field]: val } : a)) }));
  const addActivityRow = () => setForm((f) => ({ ...f, activities: [...f.activities, { title: "", cost: "" }] }));
  const removeActivityRow = (i) => setForm((f) => ({ ...f, activities: f.activities.length > 1 ? f.activities.filter((_, idx) => idx !== i) : f.activities }));

  const save = async () => {
    if (!form.project_id) { toast.error("Select a project first."); return; }
    const activities = form.activities.map((a) => ({ title: a.title.trim(), cost: Number(a.cost) || 0 })).filter((a) => a.title || a.cost);
    if (activities.length === 0) { toast.error("Add at least one activity."); return; }
    const cost = activities.reduce((s, a) => s + a.cost, 0);
    if (cost <= 0) { toast.error("Total cost must be greater than zero."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/additional-works", { project_id: form.project_id, title: form.title.trim(), remarks: form.remarks.trim(), activities, cost });
      if (data.pending_approval) toast.info(data.message || "Submitted to the CEO for approval");
      else toast.success("Additional work recorded");
      setOpen(false);
      qc.invalidateQueries();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  const remove = async (w) => {
    try { await api.delete(`/additional-works/${w.id}`); toast.success("Additional work deleted"); qc.invalidateQueries(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Additional Works" subtitle="Extra scope added to projects — folded into each project's contract value">
        {canCreate && <Button data-testid="aw-add-button" onClick={openNew} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Plus className="h-4 w-4 mr-2" /> Add Additional Work</Button>}
      </PageHeader>

      {isLoading ? <TableSkeleton /> : works.length === 0 ? (
        <EmptyState icon={Hammer} title="No additional works yet" description="Record extra activities requested on a project. The cost is added to the project's total contract value." />
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="aw-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Activities</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Added by</TableHead>
                <TableHead>Date</TableHead>
                {canDelete && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {works.map((w) => (
                <TableRow key={w.id} data-testid={`aw-row-${w.id}`}>
                  <TableCell className="font-medium text-slate-800">{w.project_name}</TableCell>
                  <TableCell>{w.title || "—"}</TableCell>
                  <TableCell className="max-w-[220px]">
                    <span className="text-xs text-slate-500">{(w.activities || []).map((a) => a.title).filter(Boolean).join(", ") || "—"}</span>
                  </TableCell>
                  <TableCell className="text-right font-mono-num">{fmtFCFA(w.cost)}</TableCell>
                  <TableCell><StatusPill status={w.status} testId="aw-status" /></TableCell>
                  <TableCell className="text-sm text-slate-500">{w.created_by_name}</TableCell>
                  <TableCell className="text-sm text-slate-500">{fmtDate(w.created_at)}</TableCell>
                  {canDelete && (
                    <TableCell>
                      <Button variant="ghost" size="icon" data-testid={`aw-delete-${w.id}`} onClick={() => remove(w)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="aw-form-dialog">
          <DialogHeader><DialogTitle>Additional Works</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div className="space-y-1.5">
              <Label className="text-sm">Project</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm((f) => ({ ...f, project_id: v }))}>
                <SelectTrigger data-testid="aw-project-select"><SelectValue placeholder="Select a project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`aw-project-option-${p.id}`}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Work Title</Label>
              <Input data-testid="aw-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Extra socket points, first floor" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Activities</Label>
              <div className="space-y-2" data-testid="aw-activities">
                {form.activities.map((a, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`aw-activity-row-${i}`}>
                    <Input data-testid={`aw-activity-title-${i}`} className="flex-1" value={a.title} onChange={(e) => setActivity(i, "title", e.target.value)} placeholder={`Activity ${i + 1}`} />
                    <Input data-testid={`aw-activity-cost-${i}`} className="w-32" type="number" value={a.cost} onChange={(e) => setActivity(i, "cost", e.target.value)} placeholder="Cost" />
                    <button type="button" data-testid={`aw-activity-remove-${i}`} onClick={() => removeActivityRow(i)} className="shrink-0 text-slate-400 hover:text-red-600 disabled:opacity-30" disabled={form.activities.length <= 1}><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" data-testid="aw-add-activity" onClick={addActivityRow}><Plus className="h-3.5 w-3.5 mr-1.5" /> Add activity</Button>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
              <span className="text-sm text-slate-500">Total cost of additional work</span>
              <span className="font-semibold font-mono-num text-[#1E3A8A]" data-testid="aw-total-cost">{fmtFCFA(totalCost(form))}</span>
            </div>
            {selectedProject && (
              <div className="rounded-lg border border-blue-100 bg-blue-50/60 px-4 py-3 space-y-1.5" data-testid="aw-total-preview">
                <div className="flex items-center justify-between text-sm"><span className="text-slate-500">Current project total</span><span className="font-mono-num">{fmtFCFA(baseTotal)}</span></div>
                <div className="flex items-center justify-between text-sm"><span className="text-slate-500">+ This additional work</span><span className="font-mono-num">{fmtFCFA(totalCost(form))}</span></div>
                <div className="flex items-center justify-between text-sm font-semibold border-t border-blue-100 pt-1.5"><span className="text-slate-700">New project total</span><span className="font-mono-num text-emerald-700" data-testid="aw-new-total">{fmtFCFA(newTotal)}</span></div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-sm">Remarks</Label>
              <Textarea data-testid="aw-remarks" value={form.remarks} onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))} placeholder="Optional context or justification" />
            </div>
            {needsApproval && <p className="text-xs text-amber-600" data-testid="aw-approval-note">As {user?.role}, this will be sent to the CEO for approval before it is recorded.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="aw-form-submit" onClick={save} disabled={saving || !form.project_id} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">
              {needsApproval ? "Submit for Approval" : "Record Work"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

